import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { openPdf } from '../../utils/pdfHelpers';

/** En pantallas chicas no hay lugar para la vista previa: el PDF se abre aparte */
export const conVistaPrevia = () =>
  typeof window === 'undefined' || !window.matchMedia ? true : window.matchMedia('(min-width: 768px)').matches;

/**
 * Vista previa de un PDF generado en el server. `generar` descarta las
 * respuestas viejas (si se tocan dos opciones seguidas gana la última).
 */
export function useVistaPdf() {
  const [url, setUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pedido = useRef(0);
  const urlRef = useRef<string | null>(null);

  const limpiar = useCallback(() => {
    pedido.current++;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    setUrl(null);
    setBlob(null);
    setGenerando(false);
    setError(null);
  }, []);

  const generar = useCallback(async (pedir: () => Promise<Blob>): Promise<Blob | null> => {
    const n = ++pedido.current;
    setGenerando(true);
    setError(null);
    try {
      const nuevo = await pedir();
      if (n !== pedido.current) return null;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(nuevo);
      setUrl(urlRef.current);
      setBlob(nuevo);
      return nuevo;
    } catch {
      if (n === pedido.current) setError('No se pudo armar la vista previa');
      return null;
    } finally {
      if (n === pedido.current) setGenerando(false);
    }
  }, []);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  return { url, blob, generando, error, generar, limpiar };
}

/**
 * Diálogo de documento (informe de servicio, resumen mensual): vista previa
 * del PDF a la izquierda, opciones a la derecha y el envío fijo abajo.
 */
const DocumentoDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  titulo: string;
  subtitulo?: React.ReactNode;
  vista: ReturnType<typeof useVistaPdf>;
  archivo: string;
  /** Genera el PDF cuando todavía no hay vista previa (celular) */
  pedirPdf: () => Promise<Blob>;
  puedeAbrir?: boolean;
  children: React.ReactNode;
  pie?: React.ReactNode;
}> = ({ open, onOpenChange, titulo, subtitulo, vista, archivo, pedirPdf, puedeAbrir = true, children, pie }) => {
  const [abriendo, setAbriendo] = useState(false);

  const abrir = async () => {
    if (vista.blob && !vista.generando) { openPdf({ blob: vista.blob, filename: archivo }); return; }
    setAbriendo(true);
    try {
      const b = await vista.generar(pedirPdf);
      if (b) openPdf({ blob: b, filename: archivo });
    } finally {
      setAbriendo(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[92vh] w-[96vw] max-w-6xl flex-col gap-0 overflow-hidden p-0 md:grid md:grid-cols-[minmax(0,1fr)_380px]">
        <section className="relative hidden min-h-0 bg-muted/60 md:block" aria-label="Vista previa del PDF">
          {vista.url && (
            <iframe src={`${vista.url}#toolbar=0&navpanes=0&view=FitH`} aria-label="Vista previa del PDF" className="h-full w-full border-0" />
          )}
          {(vista.generando || !vista.url) && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/70">
              {vista.error ? (
                <p className="text-sm text-destructive">{vista.error}</p>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Armando la vista previa…
                </div>
              )}
            </div>
          )}
        </section>

        <aside className="flex min-h-0 flex-1 flex-col bg-background md:border-l">
          <DialogHeader className="space-y-1 border-b p-5 pr-12 text-left">
            <DialogTitle className="text-lg">{titulo}</DialogTitle>
            {subtitulo && <DialogDescription className="text-sm">{subtitulo}</DialogDescription>}
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            {children}
            <Button type="button" variant="outline" size="sm" className="w-full" onClick={abrir} disabled={!puedeAbrir || abriendo}>
              <ArrowTopRightOnSquareIcon className="mr-1.5 h-4 w-4" />
              {abriendo ? 'Generando…' : <><span className="md:hidden">Ver PDF</span><span className="hidden md:inline">Abrir para imprimir o descargar</span></>}
            </Button>
          </div>
          {pie && <div className="border-t bg-muted/30 p-5">{pie}</div>}
        </aside>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentoDialog;
